import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { BASIC_LINE, BASIC_WHITE } from "../../../constants/colors";
import { Menu } from "../../../ducks/menu";
import { Button } from "../../atoms/Button";
import { MenuItemRecommendation } from "./MenuItemRecommendation";

type Props = {
  isShow: boolean;
  showAppCouponHeader: boolean;
  menus: Menu[];
  selectedLimitedMenus: Menu[];
  onFavorite: (menuId: number, menuCode: string, favorite: boolean) => void;
  onSelectMenu: (args: {
    menuId: number;
    menuName: string;
    amount: number;
    isEarlyBird: boolean;
    optionId?: number;
    optionName?: string;
  }) => void;
  onClose: () => void;
};

export const RecommendedMenuPopup: React.FC<Props> = ({
  isShow,
  showAppCouponHeader,
  menus,
  selectedLimitedMenus,
  onFavorite,
  onSelectMenu,
  onClose,
}) => {
  const [selectedItems, setSelectedItems] = useState<
    Record<
      number,
      {
        amount: number;
        optionId?: number;
        optionName?: string;
      }
    >
  >({});

  const [shouldReset, setShouldReset] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const totalQuantity = Object.values(selectedItems).reduce(
      (acc, item) => acc + item.amount,
      0,
    );
    setIsEmpty(totalQuantity === 0);
  }, [selectedItems]);

  const handleShouldReset = useCallback(() => {
    setShouldReset(true);
    setTimeout(() => {
      setShouldReset(false);
    }, 100);
  }, []);

  const handleChangeAmount = useCallback(
    (
      menuId: number,
      amount: number,
      optionId?: number,
      optionName?: string,
    ) => {
      setSelectedItems((prev) => ({
        ...prev,
        [menuId]: {
          amount,
          optionId,
          optionName,
        },
      }));
    },
    [],
  );

  const handleAddToCart = useCallback(() => {
    Object.entries(selectedItems).forEach(([menuId, item]) => {
      if (item.amount > 0) {
        const menu = menus.find((m) => m.id === parseInt(menuId));
        if (menu) {
          onSelectMenu({
            menuId: menu.id,
            menuName: menu.name,
            amount: item.amount,
            optionId: item.optionId,
            optionName: item.optionName,
            isEarlyBird: menu.isEarlyBird,
          });
        }
      }
    });

    setSelectedItems({});
    handleShouldReset();
    onClose();
  }, [selectedItems, menus, onSelectMenu]);

  const handleClose = useCallback(() => {
    setSelectedItems({});
    handleShouldReset();
    onClose();
  }, [onClose]);

  const isLimitedMenuSelected = useCallback(
    (menu: Menu) => {
      if (!menu.isLimitedMenu) return false;
      return selectedLimitedMenus.some(
        (selectedMenu) => selectedMenu.code === menu.code,
      );
    },
    [selectedLimitedMenus],
  );

  const shouldDisable = useCallback((menu: Menu) => {
    return menu.isPreorder && !menu.isEnableButton;
  }, []);

  return (
    <RecommendedMenuPopupContainer $isShow={isShow}>
      <MenuPopupContainer>
        <Header $showAppCouponHeader={showAppCouponHeader}>
          <HeaderTitle>ご一緒にいかがですか</HeaderTitle>
        </Header>

        <MenuListContainer>
          {menus.map((menu) => (
            <MenuItemContainer key={menu.id}>
              <MenuItemRecommendation
                menu={menu}
                selectedLimitedMenus={selectedLimitedMenus}
                onFavorite={(menuCode, favorite) =>
                  onFavorite(menu.id, menuCode, favorite)
                }
                onSelectMenu={(selectedMenu, optionId, optionName) =>
                  onSelectMenu({
                    menuId: menu.id,
                    menuName: menu.name,
                    isEarlyBird: menu.isEarlyBird,
                    amount: selectedMenu,
                    optionId: optionId,
                    optionName: optionName,
                  })
                }
                noPopup
                disableClick
                showQuantitySelector={true}
                quantity={selectedItems[menu.id]?.amount || 0}
                onQuantityChange={(count) => handleChangeAmount(menu.id, count)}
                onAddToCart={() => {
                  const currentItem = selectedItems[menu.id];
                  if (currentItem && currentItem.amount > 0) {
                    onSelectMenu({
                      menuId: menu.id,
                      menuName: menu.name,
                      amount: currentItem.amount,
                      optionId: currentItem.optionId,
                      optionName: currentItem.optionName,
                      isEarlyBird: menu.isEarlyBird,
                    });
                    setSelectedItems((prev) => {
                      const updated = { ...prev };
                      delete updated[menu.id];
                      return updated;
                    });
                  }
                }}
                shouldReset={shouldReset}
              />
            </MenuItemContainer>
          ))}
        </MenuListContainer>

        <ButtonWrapper>
          <Button
            btnStyle="secondary"
            onClick={handleClose}
            text="他の商品を追加"
            rounded="md"
          />
        </ButtonWrapper>
      </MenuPopupContainer>
    </RecommendedMenuPopupContainer>
  );
};

const RecommendedMenuPopupContainer = styled.div<{ $isShow: boolean }>`
  position: fixed;
  top: 50px;
  left: 0;
  width: 100%;
  height: calc(100% - 50px);
  background-color: ${BASIC_WHITE};
  opacity: ${({ $isShow }) => ($isShow ? 1 : 0)};
  transform: ${({ $isShow }) =>
    $isShow ? "translate(0, 0)" : "translate(0, 100%)"};
  transition: all 0.5s cubic-bezier(0.13, 0.79, 0.36, 0.97);
  overflow: scroll;
  z-index: 1000;
`;

const MenuPopupContainer = styled.div`
  max-width: 980px;
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div<{ $showAppCouponHeader: boolean }>`
  display: flex;
  justify-content: center;
  padding: ${({ $showAppCouponHeader }) =>
    $showAppCouponHeader ? "32px 16px 16px" : "0 16px 16px"};
  margin: 45px 16px 27px;
  line-height: 1;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const HeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  padding: 0;
`;

const MenuItemContainer = styled.div`
  margin: 0;
`;

const MenuListContainer = styled.div`
  margin-bottom: 20px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 150px;
  button {
    height: auto;
    line-height: 1;
    font-weight: 600;
  }
`;
